import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles.css';

function CreateAlliance() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    banner_color: '#10b981',
    min_level: 1,
    min_serves: 0,
    open: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canCreate, setCanCreate] = useState(false);
  const [requirements, setRequirements] = useState(null);

  const accountHash = localStorage.getItem('avalw_account_hash');

  useEffect(() => {
    if (!accountHash) {
      navigate('/');
      return;
    }
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    try {
      const response = await fetch(`/api/my-alliance/${accountHash}`);
      const data = await response.json();

      if (data.alliance) {
        // Already in an alliance
        navigate(`/alliance/${data.alliance.id}`);
        return;
      }

      setCanCreate(data.can_create);
      setRequirements(data.requirements);
    } catch (err) {
      setError('Failed to check eligibility');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/alliances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_hash: accountHash,
          name: formData.name,
          description: formData.description,
          banner_color: formData.banner_color,
          join_requirement: {
            min_level: parseInt(formData.min_level),
            min_serves: parseInt(formData.min_serves),
            open: formData.open
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        navigate(`/alliance/${data.alliance.id}`);
      } else {
        setError(data.error || 'Failed to create alliance');
      }
    } catch (err) {
      setError('Failed to create alliance');
    } finally {
      setLoading(false);
    }
  };

  const colorOptions = [
    { value: '#10b981', name: 'Eco Green' },
    { value: '#3b82f6', name: 'Ocean Blue' },
    { value: '#8b5cf6', name: 'Violet' },
    { value: '#f59e0b', name: 'Amber' },
    { value: '#ef4444', name: 'Red' },
    { value: '#ec4899', name: 'Pink' },
    { value: '#14b8a6', name: 'Teal' },
    { value: '#6366f1', name: 'Indigo' }
  ];

  if (!accountHash) {
    return null;
  }

  return (
    <div className="landing-page create-alliance-page">
      {/* Navigation */}
      <nav className="nav">
        <Link to="/" className="logo">
          <span className="logo-icon">⚡</span>
          <span>AVALW Workers</span>
        </Link>
        <div className="nav-links">
          <Link to="/">Home</Link>
          <Link to="/alliances">Alliances</Link>
          <Link to="/rewards">Rewards</Link>
          {accountHash && <Link to="/dashboard">Dashboard</Link>}
        </div>
      </nav>

      {/* Create Form */}
      <section className="create-section">
        <div className="container">
          <div className="create-card">
            <div className="create-header">
              <h1>
                <span>🌱</span> Create New Alliance
              </h1>
              <p>Unite contributors to maximize environmental impact</p>
            </div>

            {!canCreate ? (
              <div className="eligibility-warning">
                <span className="warning-icon">🔒</span>
                <h3>Not Eligible Yet</h3>
                <p>
                  To create an alliance, you need to reach:
                </p>
                <ul>
                  <li>Level {requirements?.min_level || 5} or higher</li>
                  <li>OR {requirements?.min_serves?.toLocaleString() || '5,000'} serves</li>
                </ul>
                <p className="hint">
                  Keep contributing to unlock alliance creation!
                </p>
                <Link to="/dashboard" className="btn btn-primary">
                  View Your Progress
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="create-form">
                {error && (
                  <div className="form-error">
                    <span>⚠️</span> {error}
                  </div>
                )}

                {/* Alliance Name */}
                <div className="form-group">
                  <label htmlFor="name">Alliance Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter alliance name"
                    minLength={3}
                    maxLength={30}
                    required
                  />
                  <span className="char-count">{formData.name.length}/30</span>
                </div>

                {/* Description */}
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your alliance's mission..."
                    maxLength={500}
                    rows={4}
                  />
                  <span className="char-count">{formData.description.length}/500</span>
                </div>

                {/* Banner Color */}
                <div className="form-group">
                  <label>Banner Color</label>
                  <div className="color-picker">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        className={`color-option ${formData.banner_color === color.value ? 'selected' : ''}`}
                        style={{ background: color.value }}
                        onClick={() => setFormData({ ...formData, banner_color: color.value })}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="preview-section">
                  <label>Preview</label>
                  <div className="alliance-preview" style={{ '--banner-color': formData.banner_color }}>
                    <div className="preview-banner" style={{ background: formData.banner_color }}>
                      <span className="preview-level">Lv.1</span>
                    </div>
                    <div className="preview-content">
                      <h4>{formData.name || 'Alliance Name'}</h4>
                      <p>{formData.description || 'Alliance description...'}</p>
                    </div>
                  </div>
                </div>

                {/* Join Requirements */}
                <div className="form-section">
                  <h3>Join Requirements</h3>

                  <div className="form-row">
                    <div className="form-group half">
                      <label htmlFor="min_level">Minimum Level</label>
                      <input
                        type="number"
                        id="min_level"
                        value={formData.min_level}
                        onChange={(e) => setFormData({ ...formData, min_level: e.target.value })}
                        min={1}
                        max={50}
                      />
                    </div>
                    <div className="form-group half">
                      <label htmlFor="min_serves">Minimum Serves</label>
                      <input
                        type="number"
                        id="min_serves"
                        value={formData.min_serves}
                        onChange={(e) => setFormData({ ...formData, min_serves: e.target.value })}
                        min={0}
                        step={100}
                      />
                    </div>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.open}
                        onChange={(e) => setFormData({ ...formData, open: e.target.checked })}
                      />
                      <span className="checkbox-text">
                        <strong>Open Alliance</strong>
                        <small>Anyone meeting requirements can join freely</small>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Submit */}
                <div className="form-actions">
                  <Link to="/alliances" className="btn btn-secondary">
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={loading || !formData.name}
                  >
                    {loading ? 'Creating...' : '🌱 Create Alliance'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>AVALW Worker Network - Saving the planet together 🌍</p>
        </div>
      </footer>
    </div>
  );
}

export default CreateAlliance;
